import React, { useEffect, useState } from "react"
import { Modal } from "reactstrap"
import Swal from "sweetalert2"
import axios from "axios"
import html2pdf from "html2pdf.js"
import configure from "configure"
import secureLocalStorage from "react-secure-storage"
import "./EmailInvoiceModal.css"

const MANDATORY_CC = "accounts@zithas.com"
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseEmailList = raw =>
  (raw || "")
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean)

const buildDefaultMessage = invoiceNumber =>
  `Dear Customer,

Please find attached Invoice **${invoiceNumber || ""}** for your reference and necessary action.

Kindly review the invoice and please process the payment.

Should you require any clarification regarding the invoice, please feel free to contact our Accounts Team.

Thanks,
Accounts Team,
Logo
Web: www.zithas.com | www.zithas.in
Email: accounts@zithas.com`

// Gmail/Outlook-style inline tag field: typed text turns into a pill on
// Enter/comma/semicolon/Tab/blur, and pasting a comma-separated list splits
// it into multiple pills at once. `locked` entries (e.g. the mandatory CC)
// show without a remove control.
function EmailTagField({ chips, setChips, locked = [], placeholder }) {
  const [inputValue, setInputValue] = useState("")

  const commit = raw => {
    const parts = parseEmailList(raw)
    if (!parts.length) return

    const invalid = parts.filter(email => !EMAIL_RE.test(email))
    if (invalid.length) {
      Swal.fire({
        icon: "warning",
        title: "Invalid email",
        text: `"${invalid[0]}" is not a valid email address.`,
      })
    }

    const valid = parts.filter(email => EMAIL_RE.test(email) && !chips.includes(email))
    if (valid.length) setChips([...chips, ...valid])
  }

  const handleKeyDown = e => {
    if ((e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") && inputValue.trim()) {
      e.preventDefault()
      commit(inputValue)
      setInputValue("")
    } else if (e.key === "Backspace" && !inputValue && chips.length) {
      const last = chips[chips.length - 1]
      if (!locked.includes(last)) setChips(chips.slice(0, -1))
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      commit(inputValue)
      setInputValue("")
    }
  }

  const handlePaste = e => {
    const text = e.clipboardData.getData("text")
    if (/[,;]/.test(text)) {
      e.preventDefault()
      commit(text)
      setInputValue("")
    }
  }

  return (
    <div className="email-tag-field">
      {chips.map(email => (
        <span key={email} className="email-tag-pill">
          {email}
          {!locked.includes(email) && (
            <i
              className="mdi mdi-close"
              onClick={() => setChips(chips.filter(c => c !== email))}
            ></i>
          )}
        </span>
      ))}
      <input
        type="text"
        className="email-tag-input"
        placeholder={chips.length ? "" : placeholder}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
      />
    </div>
  )
}

const EmailInvoiceModal = ({
  isOpen,
  toggle,
  invoiceNumber,
  customerId,
  companyLabel,
  contentRef,
}) => {
  const [toChips, setToChips] = useState([])
  const [ccChips, setCcChips] = useState([MANDATORY_CC])
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const token = secureLocalStorage.getItem("auth_token")

  const fileName = `${(invoiceNumber || "invoice").replace(/\//g, "-")}.pdf`

  useEffect(() => {
    if (!isOpen) return

    setSubject(`Invoice ${invoiceNumber || ""} from Zithas Technologies`)
    setMessage(buildDefaultMessage(invoiceNumber))
    setCcChips([MANDATORY_CC])
    setToChips([])

    const fetchContact = async () => {
      if (!customerId) return
      try {
        const res = await axios.post(
          `${configure.nodeApi}/contacts`,
          { customer_id: customerId },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const contacts = res?.data?.data?.contacts || []
        const primary = contacts.find(c => Number(c.primarycontact) === 1) || contacts[0]
        if (primary?.email) setToChips([primary.email])
      } catch (err) {
        console.error("Failed to fetch customer contact:", err)
      }
    }
    fetchContact()
  }, [isOpen, invoiceNumber, customerId, companyLabel])

  const onSend = async () => {
    if (!toChips.length) {
      Swal.fire({ icon: "warning", title: "Recipient required", text: "Please add at least one recipient in To." })
      return
    }

    const ccList = Array.from(new Set([...ccChips, MANDATORY_CC]))

    if (!contentRef?.current) {
      Swal.fire({ icon: "error", title: "Failed", text: "Invoice content is not ready yet." })
      return
    }

    setSending(true)
    try {
      const pdfBase64 = await html2pdf()
        .from(contentRef.current)
        .set({
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { dpi: 192, letterRendering: true, scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .outputPdf("datauristring")

      const messageHtml = message
        .split("\n")
        .map(line =>
          line.trim() === "Logo"
            ? '<img src="cid:zithasLogo" alt="Zithas" style="height:36px;display:block;margin:6px 0;" />'
            : line
        )
        .join("\n")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br/>")

      const payload = {
        to: toChips,
        subject,
        message: messageHtml,
        pdfBase64,
        fileName,
      }
      if (ccList.length) payload.cc = ccList

      const res = await axios.post(
        `${configure.nodeApi}/sendInvoiceEmail`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (res?.data?.success) {
        Swal.fire({ icon: "success", title: "Sent", text: "Invoice emailed successfully." })
        toggle()
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: res?.data?.message || "Could not send email." })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err?.response?.data?.message || err?.message || String(err),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal isOpen={isOpen} centered toggle={toggle} modalClassName="email-invoice-modal">
      <div className="email-compose">
        <div className="email-compose-header">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-envelope text-purple"></i>
            <span className="email-compose-title">Email Invoice</span>
          </div>
          <button type="button" onClick={toggle} className="btn-modal-close">
            <span aria-hidden="true" className="btn-modal-span">&times;</span>
          </button>
        </div>

        <div className="email-compose-fields">
          <div className="email-compose-row">
            <span className="email-compose-label">To</span>
            <EmailTagField chips={toChips} setChips={setToChips} placeholder="Recipient email(s)" />
          </div>
          <div className="email-compose-row">
            <span className="email-compose-label">Cc</span>
            <EmailTagField
              chips={ccChips}
              setChips={setCcChips}
              locked={[MANDATORY_CC]}
              placeholder="Add CC email(s)"
            />
          </div>
          <div className="email-compose-row">
            <span className="email-compose-label">Subject</span>
            <input
              type="text"
              className="email-compose-input"
              placeholder="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>
        </div>

        <div className="email-compose-message">
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Write your message..."
          />
        </div>

        <div className="email-compose-attachment">
          <div className="email-attachment-card">
            <i className="fas fa-file-pdf"></i>
            <div>
              <div className="email-attachment-name">{fileName}</div>
              <div className="email-attachment-type">Invoice PDF</div>
            </div>
          </div>
        </div>

        <div className="email-compose-footer">
          <button className="btn btn-red btn-label me-1" onClick={toggle} disabled={sending}>
            <i className="dripicons-cross me-2 icon-center label-icon"></i>
            Cancel
          </button>
          <button className="btn btn-purple btn-label" onClick={onSend} disabled={sending}>
            <i className="fas fa-paper-plane me-2 icon-size label-icon"></i>
            {sending ? "Sending..." : "Send Invoice"}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default EmailInvoiceModal
